import { type KeyboardEvent, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  Calendar,
  ContactRound,
  Copy,
  Loader2,
  Pencil,
  Plus,
  Save,
  Search,
  Share2,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { Header } from '@/components/Header';
import { LinkText } from '@/components/LinkText';
import {
  MigratedCrudHistory,
  formatMigratedDateTime,
} from '@/components/MigratedCrudHistory';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import {
  usefulContactsApi,
  type SaveUsefulContactDto,
  type UsefulContact,
} from '@/api/useful-contacts';

const FIELD_LABELS: Record<string, string> = {
  name: 'Название',
  contacts: 'Контакты',
  comment: 'Комментарий',
  legacyId: 'Legacy ID',
  legacyLog: 'Legacy лог',
  legacyLastChange: 'Изменено',
  importedAt: 'Импортировано',
  createdAt: 'Создано',
  updatedAt: 'Изменено',
};

function formatDate(value?: string | null): string {
  if (!value) return '';
  return new Date(value).toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function contactText(contact: UsefulContact): string {
  return [
    `Название: ${contact.name}`,
    `Контакты: ${contact.contacts}`,
    contact.comment ? `Комментарий: ${contact.comment}` : '',
  ]
    .filter(Boolean)
    .join('\n');
}

function errorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === 'object' && 'response' in error) {
    const response = (error as { response?: { data?: { message?: string } } }).response;
    return response?.data?.message || fallback;
  }
  return error instanceof Error ? error.message : fallback;
}

async function copyContact(contact: UsefulContact) {
  await navigator.clipboard.writeText(contactText(contact));
  toast.success('Скопировано');
}

async function shareContact(contact: UsefulContact) {
  const text = contactText(contact);
  if (navigator.share) {
    await navigator.share({
      title: `Контакты ${contact.name}`,
      text,
    });
    return;
  }
  await navigator.clipboard.writeText(text);
  toast.success('Скопировано');
}

function UsefulContactCard({
  contact,
  onOpen,
}: {
  contact: UsefulContact;
  onOpen: (contact: UsefulContact) => void;
}) {
  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onOpen(contact);
    }
  };

  return (
    <article
      className="rounded-lg border border-border bg-card p-3 active:bg-accent"
      role="button"
      tabIndex={0}
      onClick={() => onOpen(contact)}
      onKeyDown={handleKeyDown}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h3 className="font-medium text-base leading-snug">{contact.name}</h3>
          {contact.updatedAt && (
            <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
              <Calendar className="w-3 h-3" />
              {formatDate(contact.updatedAt)}
            </div>
          )}
        </div>
        <div className="flex shrink-0 gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9"
            aria-label="Копировать"
            onClick={(event) => {
              event.stopPropagation();
              copyContact(contact).catch(() => toast.error('Не удалось скопировать'));
            }}
          >
            <Copy className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9"
            aria-label="Поделиться"
            onClick={(event) => {
              event.stopPropagation();
              shareContact(contact).catch(() => undefined);
            }}
          >
            <Share2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="mt-3 whitespace-pre-wrap break-words text-sm">{contact.contacts}</div>
      {contact.comment && (
        <div className="mt-3 whitespace-pre-wrap break-words border-t border-border pt-3 text-sm text-muted-foreground">
          {contact.comment}
        </div>
      )}
    </article>
  );
}

function DetailField({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;

  return (
    <div>
      <div className="mb-1 text-xs font-medium uppercase tracking-normal text-muted-foreground">
        {label}
      </div>
      <div className="whitespace-pre-wrap break-words text-sm">
        <LinkText>{value}</LinkText>
      </div>
    </div>
  );
}

function UsefulContactAuditHistory({
  legacyLog,
  contactId,
  open,
}: {
  legacyLog?: string | null;
  contactId: string;
  open: boolean;
}) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['useful-contact-history', contactId],
    queryFn: () => usefulContactsApi.getHistory(contactId),
    enabled: open,
  });

  const items = data?.items || [];

  return (
    <MigratedCrudHistory
      legacyLog={legacyLog}
      auditItems={items}
      total={data?.total}
      isLoading={isLoading}
      error={error}
      fieldLabels={FIELD_LABELS}
    />
  );
}

function UsefulContactDetailSheet({
  contact,
  open,
  onOpenChange,
  onEdit,
}: {
  contact: UsefulContact | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit: (contact: UsefulContact) => void;
}) {
  if (!contact) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[90vh] overflow-y-auto">
        <SheetHeader className="pr-8 text-left">
          <SheetTitle className="flex items-center gap-2">
            <ContactRound className="h-5 w-5" />
            {contact.name}
          </SheetTitle>
          <SheetDescription>
            Изменено: {formatMigratedDateTime(contact.updatedAt)}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          <div className="flex gap-2">
            <Button className="flex-1" onClick={() => onEdit(contact)}>
              <Pencil className="h-4 w-4" />
              Редактировать
            </Button>
            <Button
              variant="outline"
              size="icon"
              aria-label="Копировать"
              onClick={() => copyContact(contact).catch(() => toast.error('Не удалось скопировать'))}
            >
              <Copy className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              aria-label="Поделиться"
              onClick={() => shareContact(contact).catch(() => undefined)}
            >
              <Share2 className="h-4 w-4" />
            </Button>
          </div>

          <div className="space-y-3 rounded-lg border border-border p-3">
            <DetailField label="Название" value={contact.name} />
            <DetailField label="Контакты" value={contact.contacts} />
            <DetailField label="Комментарий" value={contact.comment} />
          </div>

          <div className="grid grid-cols-2 gap-2 rounded-lg border border-border p-3 text-xs">
            {contact.legacyId !== null && (
              <>
                <span className="text-muted-foreground">Legacy ID</span>
                <span className="text-right">{contact.legacyId}</span>
              </>
            )}
            <span className="text-muted-foreground">Создано</span>
            <span className="text-right">{formatMigratedDateTime(contact.createdAt)}</span>
            <span className="text-muted-foreground">Изменено</span>
            <span className="text-right">{formatMigratedDateTime(contact.updatedAt)}</span>
          </div>

          <UsefulContactAuditHistory
            legacyLog={contact.legacyLog}
            contactId={contact.id}
            open={open}
          />
        </div>
      </SheetContent>
    </Sheet>
  );
}

function UsefulContactFormSheet({
  contact,
  open,
  onOpenChange,
  onSaved,
}: {
  contact: UsefulContact | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: (contact: UsefulContact) => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [contacts, setContacts] = useState('');
  const [comment, setComment] = useState('');

  useEffect(() => {
    if (!open) return;
    setName(contact?.name || '');
    setContacts(contact?.contacts || '');
    setComment(contact?.comment || '');
  }, [contact, open]);

  const saveMutation = useMutation({
    mutationFn: () => {
      const body: SaveUsefulContactDto = {
        name: name.trim(),
        contacts: contacts.trim(),
        comment: comment.trim(),
      };
      return contact
        ? usefulContactsApi.update(contact.id, body)
        : usefulContactsApi.create(body);
    },
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: ['useful-contacts'] });
      queryClient.invalidateQueries({ queryKey: ['useful-contact-history', saved.id] });
      toast.success('Контакт сохранен');
      onSaved(saved);
      onOpenChange(false);
    },
    onError: (error) => toast.error(errorMessage(error, 'Ошибка сохранения')),
  });

  const canSubmit =
    name.trim().length > 0 && contacts.trim().length > 0 && !saveMutation.isPending;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[90vh] overflow-y-auto">
        <SheetHeader className="pr-8 text-left">
          <SheetTitle>{contact ? 'Редактировать контакт' : 'Новый контакт'}</SheetTitle>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium">Название</label>
            <Input value={name} onChange={(event) => setName(event.target.value)} />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium">Контакты</label>
            <Textarea
              value={contacts}
              onChange={(event) => setContacts(event.target.value)}
              rows={5}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium">Комментарий</label>
            <Textarea
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              rows={4}
            />
          </div>

          <div className="flex gap-2 border-t border-border pt-4">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={() => onOpenChange(false)}
              disabled={saveMutation.isPending}
            >
              Отмена
            </Button>
            <Button
              type="button"
              className="flex-1"
              onClick={() => saveMutation.mutate()}
              disabled={!canSubmit}
            >
              {saveMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Сохранить
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export function UsefulContactsPage() {
  const [search, setSearch] = useState('');
  const [selectedContact, setSelectedContact] = useState<UsefulContact | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [formContact, setFormContact] = useState<UsefulContact | null>(null);
  const [formOpen, setFormOpen] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['useful-contacts', search],
    queryFn: () => usefulContactsApi.getAll(search),
  });

  const contacts = useMemo(() => data || [], [data]);

  const openDetail = (contact: UsefulContact) => {
    setSelectedContact(contact);
    setDetailOpen(true);
  };

  const openCreate = () => {
    setFormContact(null);
    setFormOpen(true);
  };

  const openEdit = (contact: UsefulContact) => {
    setDetailOpen(false);
    setFormContact(contact);
    setFormOpen(true);
  };

  const handleSaved = (contact: UsefulContact) => {
    setSelectedContact(contact);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header
        title="Полезные контакты"
        showBack
        rightContent={
          <Button size="icon" aria-label="Добавить" onClick={openCreate}>
            <Plus className="h-5 w-5" />
          </Button>
        }
      />

      <main className="p-4">
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Поиск"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="pl-10 pr-10"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {!isLoading && !error && (
          <p className="text-sm text-muted-foreground mb-3">Всего записей: {contacts.length}</p>
        )}

        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        )}

        {error && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <AlertCircle className="w-12 h-12 text-destructive mb-4" />
            <p className="text-muted-foreground">Ошибка загрузки</p>
          </div>
        )}

        {!isLoading && !error && contacts.length > 0 && (
          <div className="space-y-2">
            {contacts.map((contact) => (
              <UsefulContactCard key={contact.id} contact={contact} onOpen={openDetail} />
            ))}
          </div>
        )}

        {!isLoading && !error && contacts.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <ContactRound className="w-12 h-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Ничего не найдено</p>
          </div>
        )}
      </main>

      <UsefulContactDetailSheet
        contact={selectedContact}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        onEdit={openEdit}
      />

      <UsefulContactFormSheet
        contact={formContact}
        open={formOpen}
        onOpenChange={setFormOpen}
        onSaved={handleSaved}
      />
    </div>
  );
}
